import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * FileProgressContext — global, per-file progress tracker.
 *
 * Use cases:
 *   • A user clicks "Open" on a document → we fetch the bytes for the viewer.
 *   • A user clicks "Download PDF/Encrypted/Original" → we stream a blob.
 *
 * Both flows often take 0.3 – 8 s on first hit (depending on conversion
 * and document size). Without feedback the click feels dead. This context
 * exposes a tiny imperative API the callers use to surface a bottom-of-
 * screen progress bar.
 *
 * API (returned from useFileProgress()):
 *   start({ label, indeterminate? }) -> id      — adds a new task
 *   update(id, { loaded, total })               — sets % from byte counts
 *   setLabel(id, label)                         — updates the visible name
 *   complete(id)                                — marks done; auto-clears
 *   error(id, message?)                         — marks failed; auto-clears
 *
 * Internally the provider holds an ordered list of active tasks so we can
 * theoretically stack multiple, but the visual UI shows the most recent
 * task to keep the chrome quiet (matches "minimal" design goal).
 */

const FileProgressContext = createContext(null);

let _seq = 0;
const nextId = () => `fp_${Date.now()}_${++_seq}`;

const AUTO_DISMISS_MS = 700;     // after complete()
const ERROR_DISMISS_MS = 2400;   // after error()

export const FileProgressProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]); // [{ id, label, percent, status, indeterminate, error }]
  const timersRef = useRef({}); // id -> setTimeout handle

  const remove = useCallback((id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const start = useCallback(({ label, indeterminate = false } = {}) => {
    const id = nextId();
    setTasks((prev) => [
      ...prev,
      {
        id,
        label: label || "Preparing file…",
        percent: 0,
        status: "loading",
        indeterminate,
        error: null,
      },
    ]);
    return id;
  }, []);

  const update = useCallback((id, { loaded, total } = {}) => {
    if (!id) return;
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        // Axios sometimes hands us only `loaded` (chunked / no Content-Length).
        // In that case we keep the bar indeterminate so we don't fake a number.
        if (!total || total <= 0) {
          return { ...t, indeterminate: true };
        }
        const pct = Math.min(99, Math.round((loaded / total) * 100));
        return { ...t, percent: pct, indeterminate: false };
      })
    );
  }, []);

  const setLabel = useCallback((id, label) => {
    if (!id) return;
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, label } : t)));
  }, []);

  const complete = useCallback(
    (id) => {
      if (!id) return;
      setTasks((prev) => {
        // Idempotent: a finally-block `complete()` should not clobber a
        // prior `error()` call from the catch-block.
        const t = prev.find((x) => x.id === id);
        if (!t || t.status === "error") return prev;
        return prev.map((x) =>
          x.id === id ? { ...x, percent: 100, status: "complete", indeterminate: false } : x
        );
      });
      // Schedule dismissal once — if a timer is already pending (e.g. from
      // a prior error()), leave it alone.
      if (!timersRef.current[id]) {
        timersRef.current[id] = setTimeout(() => remove(id), AUTO_DISMISS_MS);
      }
    },
    [remove]
  );

  const error = useCallback(
    (id, message) => {
      if (!id) return;
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: "error", error: message || "Failed" } : t))
      );
      timersRef.current[id] = setTimeout(() => remove(id), ERROR_DISMISS_MS);
    },
    [remove]
  );

  // Tear down every pending dismiss timer on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
  }, []);

  const value = { tasks, start, update, setLabel, complete, error };
  return <FileProgressContext.Provider value={value}>{children}</FileProgressContext.Provider>;
};

export const useFileProgress = () => {
  const ctx = useContext(FileProgressContext);
  if (!ctx) {
    throw new Error("useFileProgress must be used inside <FileProgressProvider>");
  }
  return ctx;
};
